<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import SeatOrdersAPI from 'dashboard/api/seatOrders';

const PRODUCT_KEY = 'seat';

const { t } = useI18n();

const isVisible = ref(false);
const quantity = ref(1);
const isLoading = ref(false);
const error = ref(null);

// 支付页面相关状态
const showPayment = ref(false);
const paymentUrl = ref('');

// 商品配置从后端API获取
const pricePerUnit = ref(0);
const productNo = ref('');

// 计算总价
const totalPrice = computed(() => {
  return ((quantity.value * pricePerUnit.value) / 100).toFixed(2);
});

// 格式化价格显示
const formatPrice = cents => {
  return (cents / 100).toFixed(2);
};

const decreaseQuantity = () => {
  if (quantity.value > 1) {
    quantity.value -= 1;
  }
};

const increaseQuantity = () => {
  quantity.value += 1;
};

const handleQuantityInput = event => {
  const value = parseInt(event.target.value, 10);
  if (Number.isNaN(value) || value < 1) {
    quantity.value = 1;
  } else {
    quantity.value = value;
  }
};

// 从后端获取价格配置
const fetchConfig = async () => {
  try {
    const response = await SeatOrdersAPI.getConfig(PRODUCT_KEY);
    if (response.data.price_per_unit) {
      pricePerUnit.value = response.data.price_per_unit;
    }
    if (response.data.product_no) {
      productNo.value = response.data.product_no;
    }
  } catch (err) {
    // 使用空函数避免 console 警告，生产环境可接入日志系统
  }
};

// 关闭弹窗
const close = () => {
  quantity.value = 1;
  error.value = null;
  showPayment.value = false;
  paymentUrl.value = '';
  isVisible.value = false;
};

// 监听支付完成消息
const handlePaymentMessage = event => {
  try {
    // 精确匹配 origin
    const expectedOrigin = paymentUrl.value
      ? new URL(paymentUrl.value).origin
      : null;
    if (!expectedOrigin || event.origin !== expectedOrigin) {
      return;
    }
  } catch {
    return;
  }

  // 支付完成或支付页面关闭
  if (event.data === 'payment_success' || event.data === 'payment_closed') {
    close();
    // 刷新当前页面
    window.location.reload();
  }
};

onMounted(() => {
  window.addEventListener('message', handlePaymentMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handlePaymentMessage);
});

const open = async () => {
  quantity.value = 1;
  error.value = null;
  showPayment.value = false;
  paymentUrl.value = '';
  isVisible.value = true;

  // 获取价格配置
  await fetchConfig();
};

const handlePurchase = async () => {
  if (quantity.value < 1) {
    error.value = '购买数量必须大于0';
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const response = await SeatOrdersAPI.createOrder({
      quantity: quantity.value,
      parentOrigin: window.location.origin,
      productKey: PRODUCT_KEY,
    });
    const { payment_url: url } = response.data;

    // 使用 iframe 显示支付页面
    if (url) {
      paymentUrl.value = url;
      showPayment.value = true;
    }
  } catch (err) {
    error.value = err.response?.data?.error || '创建订单失败，请稍后重试';
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      @click.self="close"
    >
      <!-- 支付页面 iframe 模式 -->
      <div
        v-if="showPayment"
        class="w-[550px] max-w-[95vw] h-[550px] max-h-[85vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden flex-shrink-0"
      >
        <!-- 标题栏 -->
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0"
        >
          <h3 class="text-lg font-medium text-slate-900">
            {{ t('SEAT_ORDER.PAYMENT_TITLE') }}
          </h3>
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            @click="close"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>
        <!-- iframe 支付页面 -->
        <div class="flex-1 overflow-hidden relative">
          <iframe
            :src="paymentUrl"
            class="absolute inset-0 w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation"
          />
        </div>
      </div>

      <!-- 购买表单模式 -->
      <div v-else class="w-full max-w-md p-6 bg-white rounded-xl shadow-xl">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-slate-900">
            {{ t('SEAT_ORDER.TITLE') }}
          </h3>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            @click="close"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>

        <div class="flex flex-col gap-4">
          <!-- 产品信息 -->
          <div class="p-4 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-3 mb-2">
              <div
                class="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500"
              >
                <i class="i-lucide-users text-white text-lg" />
              </div>
              <div>
                <div class="font-medium text-slate-900">
                  {{ t('SEAT_ORDER.PRODUCT_NAME') }}
                </div>
                <div class="text-sm text-slate-500">
                  {{ t('SEAT_ORDER.PRODUCT_DESC') }}
                </div>
              </div>
            </div>
            <div
              class="flex items-center justify-between mt-3 pt-3 border-t border-gray-200"
            >
              <span class="text-sm text-slate-500">{{
                t('SEAT_ORDER.UNIT_PRICE')
              }}</span>
              <span class="font-medium text-slate-900">
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                ¥{{ formatPrice(pricePerUnit) }}
              </span>
            </div>
          </div>

          <!-- 数量选择 -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-slate-700">
              {{ t('SEAT_ORDER.QUANTITY') }}
            </label>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                :disabled="quantity <= 1"
                @click="decreaseQuantity"
              >
                <i class="i-lucide-minus text-slate-600" />
              </button>
              <input
                type="number"
                :value="quantity"
                min="1"
                class="w-20 h-10 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                @input="handleQuantityInput"
              />
              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                @click="increaseQuantity"
              >
                <i class="i-lucide-plus text-slate-600" />
              </button>
            </div>
          </div>

          <!-- 总价 -->
          <div
            class="flex items-center justify-between p-4 bg-blue-50 rounded-lg"
          >
            <span class="text-sm font-medium text-blue-700">{{
              t('SEAT_ORDER.TOTAL')
            }}</span>
            <span class="text-xl font-bold text-blue-700">
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              ¥{{ totalPrice }}
            </span>
          </div>

          <!-- 错误信息 -->
          <div v-if="error" class="text-sm text-red-500">
            {{ error }}
          </div>

          <!-- 按钮 -->
          <div class="flex gap-3 mt-2">
            <button
              type="button"
              class="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              :disabled="isLoading"
              @click="close"
            >
              {{ t('SEAT_ORDER.CANCEL') }}
            </button>
            <button
              type="button"
              class="flex-1 px-4 py-2 bg-woot-500 text-white font-medium rounded-lg hover:bg-woot-600 transition-colors disabled:opacity-50"
              :disabled="isLoading"
              @click="handlePurchase"
            >
              {{
                isLoading ? t('SEAT_ORDER.LOADING') : t('SEAT_ORDER.PURCHASE')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
